import { Button, Flex, Skeleton, Spacer } from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { PortfolioCoreBorrowingBaseChartTemplate } from '../shared/PortfolioCoreBorrowingBaseChartTemplate';
import { ConcentrationLimitsSection } from './ConcentrationLimitsSection';
import { DownloadAllChartsButton } from './DownloadAllChartsButton';
import { chartGroupMap } from './portfolioBorrowingBaseChartConfigs';
import { PortfolioEligibilityKickoutsChart } from './PortfolioBorrowingBaseEligibilityChart';
import { PortfolioExcessConcentrationChart } from './PortfolioExcessConcentrationChart';
import {
  useConcentrationLimitsData,
  useConcentrationLimitsMetadata,
} from './useConcentrationLimitsData';
import { useCreditAgreementConcentrationLimits } from './useCreditAgreementConcentrationLimits';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useQueryParams } from '~/packages/common-frontend/hooks/useQueryParams';
import { creditFacilityIdQueryParam } from '~/packages/credit-facility/ui/CreditFacilitySelect';

const borrowingBaseDisplayModes = [
  'all',
  'borrowingBase',
  'eligibility',
  'concentration',
] as const;
type BorrowingBaseChartDisplayMode = typeof borrowingBaseDisplayModes[number];
const isBorrowingBaseChartDisplayMode = (
  test: any,
): test is BorrowingBaseChartDisplayMode => {
  return (
    test &&
    typeof test === 'string' &&
    _.includes(borrowingBaseDisplayModes, test)
  );
};

const borrowingBaseDisplayModeQueryParam = 'display';

const { borrowingBase, eligibility } = chartGroupMap;

type PortfolioBorrowingBaseContainerProps = { creditFacilityId: string };
export const PortfolioBorrowingBaseContainer = ({
  creditFacilityId,
}: PortfolioBorrowingBaseContainerProps) => {
  const { respectConcentrationLimitFormat } = useFeatureFlags();
  const navigate = useNavigate();
  const query = useQueryParams();
  const queryDisplayMode = query.get(borrowingBaseDisplayModeQueryParam);
  const displayMode = isBorrowingBaseChartDisplayMode(queryDisplayMode)
    ? queryDisplayMode
    : 'all';

  // Use custom hook for concentration limits data management
  const {
    chartDataDateRange,
    setChartDataDateRange,
    selectedTimePeriod,
    concentrationLimitChartData,
    concentrationLimitFormatMap,
    concentrationData,
    isConcentrationDataLoading,
    refetchConcentrationInsightsData,
    onSelectTimePeriod,
  } = useConcentrationLimitsData(creditFacilityId);

  // Use custom hook for credit agreement concentration limits
  const {
    creditAgreementData,
    isCreditAgreementLoading,
    creditAgreementConcentrationLimits,
  } = useCreditAgreementConcentrationLimits(creditFacilityId);

  // Use the second hook to get the limitKeyToTitleMap and combinedCsvData
  const {
    limitKeyToTitleMap,
    combinedCsvData,
  } = useConcentrationLimitsMetadata(
    concentrationLimitFormatMap,
    creditAgreementConcentrationLimits,
    concentrationLimitChartData,
  );

  // @dani portfolio update this skeleton design
  if (
    !concentrationData ||
    isConcentrationDataLoading ||
    !creditAgreementData ||
    isCreditAgreementLoading
  ) {
    return <Skeleton h="700px" />;
  }

  const totalChartCounts = {
    borrowingBase: borrowingBase.length,
    eligibility: eligibility.length + 1, // 1 additional for the aggregate chart
    concentration: concentrationLimitFormatMap.size + 1, // 1 additional for the aggregate chart
  };

  const onSetDisplayMode = (
    selectedDisplayMode: BorrowingBaseChartDisplayMode,
  ) => {
    const covenantsBaseRoute = '/portfolio/borrowing-base';
    const queryParams = `?${creditFacilityIdQueryParam}=${creditFacilityId}&${borrowingBaseDisplayModeQueryParam}=${selectedDisplayMode}`;

    navigate(`${covenantsBaseRoute}${queryParams}`);
  };

  return (
    <Flex direction="column" gap="2.5rem">
      <Flex gap="0.5rem">
        <Button
          variant="primary-muted"
          colorScheme="gray"
          isActive={displayMode === 'all'}
          onClick={() => onSetDisplayMode('all')}
        >
          Show All {_.sum(Object.values(totalChartCounts))}
        </Button>
        <Button
          variant="primary-muted"
          colorScheme="gray"
          isActive={displayMode === 'borrowingBase'}
          onClick={() => onSetDisplayMode('borrowingBase')}
        >
          Borrowing Base {totalChartCounts.borrowingBase}
        </Button>
        <Button
          variant="primary-muted"
          colorScheme="gray"
          isActive={displayMode === 'eligibility'}
          onClick={() => onSetDisplayMode('eligibility')}
        >
          Eligibility {totalChartCounts.eligibility}
        </Button>
        <Button
          variant="primary-muted"
          colorScheme="gray"
          isActive={displayMode === 'concentration'}
          onClick={() => onSetDisplayMode('concentration')}
        >
          Concentration {totalChartCounts.concentration}
        </Button>
        <Spacer />
        <DownloadAllChartsButton
          creditFacilityId={creditFacilityId}
          concentrationLimitFormatMap={concentrationLimitFormatMap}
          creditAgreementConcentrationLimits={
            creditAgreementConcentrationLimits
          }
        />
      </Flex>
      {_.map(chartGroupMap, (chartMaps, group) =>
        _.map(chartMaps, (chart) => {
          return (
            (displayMode === 'all' || displayMode === group) && (
              <PortfolioCoreBorrowingBaseChartTemplate
                creditFacilityId={creditFacilityId}
                chartTitle={chart.chartTitle}
                chartConfig={chart.config}
                key={`portfolio-borrowing-base-chart-${chart.chartTitle}`}
              />
            )
          );
        }),
      )}
      {(displayMode === 'all' || displayMode === 'eligibility') && (
        <PortfolioEligibilityKickoutsChart
          creditFacilityId={creditFacilityId}
          chartTitle="Eligibility Kickouts"
        />
      )}

      {(displayMode === 'all' || displayMode === 'concentration') && (
        <PortfolioExcessConcentrationChart
          creditFacilityId={creditFacilityId}
          chartTitle="Excess Concentration Amounts"
          concentrationLimitFormatMap={concentrationLimitFormatMap}
          creditAgreementConcentrationLimits={
            creditAgreementConcentrationLimits
          }
        />
      )}
      {(displayMode === 'all' || displayMode === 'concentration') &&
        concentrationLimitFormatMap.size > 0 && (
          <ConcentrationLimitsSection
            creditFacilityId={creditFacilityId}
            chartDataDateRange={chartDataDateRange}
            setChartDataDateRange={setChartDataDateRange}
            selectedTimePeriod={selectedTimePeriod}
            onSelectTimePeriod={onSelectTimePeriod}
            refetchConcentrationInsightsData={refetchConcentrationInsightsData}
            concentrationLimitFormatMap={concentrationLimitFormatMap}
            concentrationLimitChartData={concentrationLimitChartData}
            creditAgreementConcentrationLimits={
              creditAgreementConcentrationLimits
            }
            limitKeyToTitleMap={limitKeyToTitleMap}
            combinedCsvData={combinedCsvData}
            respectConcentrationLimitFormat={respectConcentrationLimitFormat}
          />
        )}
    </Flex>
  );
};
